function stripFence(value) {
  return value.trim().replace(/^```(?:json)?\s*([\s\S]*?)\s*```$/i, "$1").trim();
}

function parseJson(value) {
  const candidates = [value];
  const first = value.indexOf("{");
  const last = value.lastIndexOf("}");
  if (first >= 0 && last > first) candidates.push(value.slice(first, last + 1));
  for (const candidate of candidates) {
    try {
      return JSON.parse(candidate);
    } catch {
      // Try the extracted object before falling back to tolerant field parsing.
    }
  }
  return null;
}

function decodeFragment(value) {
  try {
    return JSON.parse(`"${value.replace(/\r/g, "\\r").replace(/\n/g, "\\n").replace(/\t/g, "\\t")}"`);
  } catch {
    return value
      .replace(/\\n/g, "\n")
      .replace(/\\r/g, "\r")
      .replace(/\\t/g, "\t")
      .replace(/\\"/g, '"')
      .replace(/\\\\/g, "\\");
  }
}

function readAnswer(raw) {
  let value = stripFence(raw);
  for (let depth = 0; depth < 3; depth += 1) {
    const parsed = parseJson(value);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) return parsed;
    if (typeof parsed !== "string") break;
    value = stripFence(parsed);
  }

  const shortMatch = value.match(/"short_answer"\s*:\s*"([\s\S]*?)"\s*,\s*"long_answer"\s*:/i);
  const longMatch = value.match(/"long_answer"\s*:\s*"([\s\S]*?)"\s*(?:[,}]|$)/i)
    ?? value.match(/"long_answer"\s*:\s*"([\s\S]*)$/i);
  if (!shortMatch && !longMatch) return null;
  return {
    short_answer: shortMatch ? decodeFragment(shortMatch[1]) : "",
    long_answer: longMatch ? decodeFragment(longMatch[1]) : "",
  };
}

function canonicalIndex(value) {
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  if (typeof value === "string" && /^\d+$/.test(value.trim())) return String(Number(value.trim()));
  return null;
}

export function normalizeConversationMessage(message) {
  const raw = typeof message.content === "string" ? message.content : "";
  let shortAnswer = raw;
  let longAnswer = "";
  if (message.role === "assistant") {
    const parsed = readAnswer(raw);
    const short = typeof parsed?.short_answer === "string" ? parsed.short_answer.trim() : "";
    const long = typeof parsed?.long_answer === "string" ? parsed.long_answer.trim() : "";
    if (short || long) {
      shortAnswer = short || long;
      longAnswer = short ? long : "";
    }
  }

  const sources = message.source_references ?? message.sourceReferences ??
    (Array.isArray(message.kms_references) ? message.kms_references : []);
  return {
    shortAnswer,
    longAnswer,
    sourceReferences: Array.isArray(sources)
      ? sources.filter(source => source && typeof source === "object").map((source, index) => ({
          ...source,
          index: typeof source.index === "number" || typeof source.index === "string" ? source.index : index + 1,
          title: source.title || source.external_id || source.document_id || `Source ${index + 1}`,
          excerpt: typeof source.excerpt === "string" ? source.excerpt : "",
        }))
      : [],
  };
}

export function getDisplayReferences(sources, limit = 5) {
  if (!Array.isArray(sources) || limit <= 0) return [];
  return sources
    .map((source, position) => {
      const raw = source.relevanceScore ?? source.relevance_score ?? source.score;
      const number = typeof raw === "number" ? raw : typeof raw === "string" && raw.trim() ? Number(raw) : Number.NaN;
      return { source, position, score: Number.isFinite(number) ? number : null };
    })
    .sort((a, b) => {
      if (a.score !== null && b.score !== null) return b.score - a.score || a.position - b.position;
      if (a.score !== null) return -1;
      if (b.score !== null) return 1;
      return a.position - b.position;
    })
    .slice(0, limit)
    .map(({ source }, position) => ({ ...source, displayIndex: position + 1 }));
}

export function rewriteCitations(text, allSources, displaySources) {
  if (typeof text !== "string" || !text || !Array.isArray(allSources)) return text;
  const known = new Set(allSources.map(source => canonicalIndex(source.index)).filter(Boolean));
  if (!known.size) return text;
  const displayByOriginal = new Map();
  for (const source of displaySources || []) {
    const original = canonicalIndex(source.index);
    if (original && !displayByOriginal.has(original)) displayByOriginal.set(original, source.displayIndex);
  }

  return text.replace(/([ \t]*)\[(\d+(?:\s*,\s*\d+)*)\]/g, (match, spacing, group) => {
    let changed = false;
    const indexes = [];
    for (const token of group.split(",")) {
      const original = canonicalIndex(token);
      if (displayByOriginal.has(original)) {
        const display = String(displayByOriginal.get(original));
        indexes.push(display);
        if (display !== token.trim()) changed = true;
      } else if (known.has(original)) {
        changed = true;
      } else {
        indexes.push(token.trim());
      }
    }
    if (!changed) return match;
    return indexes.length ? `${spacing}[${indexes.join(", ")}]` : "";
  });
}

export function getSafeHttpUrl(value) {
  if (typeof value !== "string" || !value.trim()) return null;
  const candidate = value.trim();
  try {
    const url = new URL(candidate);
    return url.protocol === "http:" || url.protocol === "https:" ? candidate : null;
  } catch {
    return null;
  }
}

function splitSuffix(candidate) {
  let url = candidate;
  let suffix = "";
  while (url && /[.,!?;:]/.test(url.at(-1))) {
    suffix = url.at(-1) + suffix;
    url = url.slice(0, -1);
  }
  const citation = url.match(/(?:\[\d+(?:,\d+)*\])+$/);
  if (citation) {
    url = url.slice(0, -citation[0].length);
    suffix = citation[0] + suffix;
  }
  while (url && /[.,!?;:]/.test(url.at(-1))) {
    suffix = url.at(-1) + suffix;
    url = url.slice(0, -1);
  }
  for (const [opening, closing] of [["(", ")"], ["[", "]"], ["{", "}"]]) {
    while (url.at(-1) === closing && url.split(closing).length > url.split(opening).length) {
      suffix = closing + suffix;
      url = url.slice(0, -1);
    }
  }
  return { url, suffix };
}

export function tokenizeHttpUrls(text) {
  if (typeof text !== "string" || !text) return [{ type: "text", value: text || "" }];
  const tokens = [];
  const pattern = /https?:\/\/[^\s<>"']+/gi;
  let cursor = 0;
  let match;
  while ((match = pattern.exec(text)) !== null) {
    if (match.index > cursor) tokens.push({ type: "text", value: text.slice(cursor, match.index) });
    const { url, suffix } = splitSuffix(match[0]);
    const href = getSafeHttpUrl(url);
    if (href) {
      tokens.push({ type: "link", value: url, href });
      if (suffix) tokens.push({ type: "text", value: suffix });
    } else {
      tokens.push({ type: "text", value: match[0] });
    }
    cursor = pattern.lastIndex;
  }
  if (cursor < text.length) tokens.push({ type: "text", value: text.slice(cursor) });
  return tokens.length ? tokens : [{ type: "text", value: text }];
}
